import { FeatureState } from '@etrigan/feature-toggles-client'
import { isBauhausSidebar } from '@news-mono/component-library'
import {
    CardItem,
    CollectionEvent,
    createCollectionAvailableEvent,
    isPublicationCardItem,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { ListCard } from '../../cards/ListCard/ListCard'
import { SNEntCard } from '../../cards/SNEntCard/SNEntCard'
import {
    StyledGrid,
    StyledGridColumn,
    StyledGridHeaderRow,
    StyledGridRow,
    StyledLandscapeItem,
    StyledList,
    StyledSectionHeader,
    StyledSimpleHeader,
} from '../../collections/November/November.styled'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import { SectionHeaderProps } from '../../section-header/SectionHeader/SectionHeader'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { CardSwitcher } from '../../__helpers/CardSwitcher'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { OverrideThemeSection } from '../../__helpers/override-theme-section'
import { getBauhausThemeOverride } from '../../__styling/get-bauhaus-theme-override'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { Section } from '../../__styling/settings/sections'
import { ThemedRender } from '../../__styling/themed-render'
import { invertMaybeLoadedItems } from '../helpers/loading'

export interface NovemberProps {
    className?: string
    timestamp?: TimestampType
    hasBackground?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    sectionHeader?: SectionHeaderProps
    section?: Section
    disableImpressionEvent?: boolean
    cardNumberOffset?: number
    noStretch?: boolean
    truncateListCards?: boolean
    onEvent: (event: CollectionEvent) => void
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
    contentIsTangential?: boolean
    currentPath?: string
    toggles?: FeatureState
}

export class November extends React.Component<NovemberProps> {
    static displayName = 'November'

    renderListCards() {
        const { timestamp, truncateListCards, onEvent } = this.props
        const items = invertMaybeLoadedItems(
            this.props.items,
            this.props.expectedCards,
        )
        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            key={cardNum}
                            item={item}
                            cardContext="november-list"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <ListCard
                                    key={index}
                                    item={publicationItem}
                                    timestamp={timestamp}
                                    onEvent={onEvent}
                                    cardNumber={cardNum}
                                    isTruncated={truncateListCards}
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    renderBauhausListCards() {
        const { timestamp, truncateListCards, onEvent, currentPath, toggles } =
            this.props
        const items = invertMaybeLoadedItems(
            this.props.items,
            this.props.expectedCards,
        )

        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false

        return (
            <React.Fragment>
                <div>
                    {items.map((item, index) => {
                        const cardNum = index + 1
                        return (
                            <CardSwitcher
                                key={cardNum}
                                item={item}
                                cardContext="november-list"
                                cardNumber={cardNum}
                                publicationCard={(publicationItem) => {
                                    if (
                                        publicationItem.loaded &&
                                        isPublicationCardItem(
                                            publicationItem.result,
                                        )
                                    ) {
                                        const themeOverride =
                                            getBauhausThemeOverride(
                                                String(currentPath),
                                            )
                                        if (isBauhaus) {
                                            return (
                                                <ThemeOverrider
                                                    override={themeOverride}
                                                >
                                                    <SNEntCard
                                                        item={publicationItem}
                                                        cardNumber={cardNum}
                                                        onEvent={onEvent}
                                                        teaserMode="hidden"
                                                        cardType="small"
                                                        verticalSpacing="md"
                                                        hasPadding={true}
                                                    />
                                                </ThemeOverrider>
                                            )
                                        } else {
                                            return (
                                                <ListCard
                                                    key={index}
                                                    item={publicationItem}
                                                    timestamp={timestamp}
                                                    onEvent={onEvent}
                                                    cardNumber={cardNum}
                                                    isTruncated={
                                                        truncateListCards
                                                    }
                                                />
                                            )
                                        }
                                    } else {
                                        return null
                                    }
                                }}
                            />
                        )
                    })}
                </div>
            </React.Fragment>
        )
    }

    renderLandscapeCards() {
        const { timestamp, onEvent } = this.props
        const items = invertMaybeLoadedItems(
            this.props.items,
            this.props.expectedCards,
        )

        return (
            <React.Fragment>
                {items.map((item, index) => {
                    const cardNum = index + 1
                    return (
                        <CardSwitcher
                            key={cardNum}
                            item={item}
                            cardContext="november-landscape"
                            cardNumber={cardNum}
                            publicationCard={(publicationItem) => (
                                <StyledLandscapeItem
                                    key={cardNum}
                                    item={publicationItem}
                                    mediaMode="hidden"
                                    asListElement
                                    cardNumber={
                                        cardNum +
                                        (this.props.cardNumberOffset || 0)
                                    }
                                    onEvent={onEvent}
                                    timestamp={timestamp}
                                    hideByline
                                    teaserMode="hidden"
                                />
                            )}
                        />
                    )
                })}
            </React.Fragment>
        )
    }

    renderSectionHeader() {
        const { sectionHeader, currentPath, toggles } = this.props
        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false
        // Only render section header when there are items and it's specified
        return (
            sectionHeader &&
            (!this.props.items.loaded || this.props.items.result.length) && (
                <StyledGridHeaderRow canGrow={sectionHeader.canGrow}>
                    <StyledGridColumn>
                        {isBauhaus ? (
                            <StyledSimpleHeader>
                                {sectionHeader.heading}
                            </StyledSimpleHeader>
                        ) : (
                            <StyledSectionHeader
                                {...sectionHeader}
                                canGrow={sectionHeader.canGrow || true}
                                showClock={true}
                            />
                        )}
                    </StyledGridColumn>
                </StyledGridHeaderRow>
            )
        )
    }

    renderNovember() {
        const {
            className,
            verticalSpacing,
            contentIsTangential,
            currentPath,
            toggles,
        } = this.props
        const isBauhaus = toggles
            ? isBauhausSidebar(toggles, currentPath)
            : false

        return (
            <ImpressionAvailable
                loading={!this.props.items.loaded}
                available={() => {
                    if (this.props.disableImpressionEvent) {
                        return
                    }
                    if (!this.props.items.loaded) {
                        console.warn(
                            'Available should never be called when loading is true',
                        )
                        return
                    }
                    this.props.onEvent(
                        createCollectionAvailableEvent(
                            this.props.items.result,
                            'November',
                        ),
                    )
                }}
            >
                {(ref) => (
                    <StyledGrid
                        ref={ref}
                        className={className}
                        verticalSpacing={verticalSpacing}
                        noStretch={this.props.noStretch}
                        as={contentIsTangential ? 'aside' : 'div'}
                    >
                        {this.renderSectionHeader()}
                        <StyledGridRow hasBackground={this.props.hasBackground}>
                            <StyledGridColumn>
                                <StyledList
                                    removeBorderLast={
                                        isBauhaus
                                            ? true
                                            : this.props.hasBackground
                                    }
                                >
                                    <ThemedRender
                                        perthnow={() => this.renderListCards()}
                                        sevennews={() =>
                                            isBauhaus
                                                ? this.renderBauhausListCards()
                                                : this.renderListCards()
                                        }
                                        thewest={() =>
                                            this.renderLandscapeCards()
                                        }
                                    />
                                </StyledList>
                            </StyledGridColumn>
                        </StyledGridRow>
                    </StyledGrid>
                )}
            </ImpressionAvailable>
        )
    }

    render() {
        const { section } = this.props
        return section ? (
            <OverrideThemeSection section={section}>
                {this.renderNovember()}
            </OverrideThemeSection>
        ) : (
            this.renderNovember()
        )
    }
}
